import React, { useMemo, useState } from 'react';
import { Box, Popover, Typography, Divider } from '@mui/material';
import { GHOST, SEQ, DOW } from './vizTokens';

interface SessionCell {
  _id: string;
  className: string;
  classType: string;
  sessionType: string;
  time: string;
  durationMins: number;
  booked: number;
  slots: number;
  fillRate: number | null;
}
interface DayEntry { date: string; sessions: SessionCell[] }

/**
 * A year of teaching, as month blocks small enough to see at once.
 *
 * ── WHY THE CELLS ARE FIXED-SIZE AND THE MONTHS TILE ────────────────────────
 * The first version gave each cell `aspectRatio: 1` inside a full-width
 * seven-column grid and stacked the months. On a wide screen that made every
 * day a ~250px square and every month a full page — a twelve-month range was
 * several minutes of scrolling to read 365 numbers, and no two months were ever
 * on screen together, which is exactly the comparison a calendar exists to
 * support.
 *
 * Now: a cell is a fixed ~30px, a month is a ~220px block, and the blocks tile
 * to fill the width. Twelve months land in three or four rows and the shape of
 * the year — the busy season, the fortnight off — is visible in one look.
 *
 * ── THE HEAT BANDS ARE TUNED TO REAL DATA, NOT TO A ROUND NUMBER ────────────
 * Bands of 30/50/70/90 looked reasonable and were useless: real studio fill
 * sits between 5% and 40%, so every day landed on the same step and the ramp
 * carried no information at all (visible in the first screenshot — an entire
 * month of identical blue). The bands below are even 20-point steps across the
 * whole range, so the part of the scale where the data actually lives has four
 * distinct steps instead of one.
 */

/** Even 20-point bands. Index 0 is reserved for "sessions but nothing booked". */
const BANDS = [
  { max: 1, step: SEQ[0], label: '0%' },
  { max: 20, step: SEQ[1], label: '<20%' },
  { max: 40, step: SEQ[2], label: '20–40%' },
  { max: 60, step: SEQ[3], label: '40–60%' },
  { max: 80, step: SEQ[4], label: '60–80%' },
  { max: Infinity, step: SEQ[5], label: '80%+' },
];

const bandFor = (fill: number | null) =>
  fill == null ? null : BANDS.find((b) => fill < b.max) ?? BANDS[BANDS.length - 1];

const ScheduleCalendar: React.FC<{ days: DayEntry[] }> = ({ days }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [active, setActive] = useState<DayEntry | null>(null);

  const byDate = useMemo(() => new Map(days.map((d) => [d.date, d])), [days]);

  const months = useMemo(() => {
    if (!days.length) return [];
    const keys = days.map((d) => d.date).sort();
    const first = new Date(`${keys[0]}T00:00:00Z`);
    const last = new Date(`${keys[keys.length - 1]}T00:00:00Z`);
    const out: { label: string; cells: (string | null)[]; sessions: number }[] = [];
    const cursor = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1));
    while (cursor <= last) {
      const y = cursor.getUTCFullYear();
      const m = cursor.getUTCMonth();
      const start = new Date(Date.UTC(y, m, 1));
      const inMonth = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
      const cells: (string | null)[] = Array(start.getUTCDay()).fill(null);
      let sessions = 0;
      for (let dd = 1; dd <= inMonth; dd++) {
        const key = new Date(Date.UTC(y, m, dd)).toISOString().slice(0, 10);
        cells.push(key);
        sessions += byDate.get(key)?.sessions.length ?? 0;
      }
      while (cells.length % 7 !== 0) cells.push(null);
      out.push({
        // Short month + 2-digit year: the block is 220px wide and
        // "December 2025" does not fit beside a session count.
        label: start.toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' }),
        cells,
        sessions,
      });
      cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    }
    return out;
  }, [days, byDate]);

  const dayFill = (d: DayEntry | undefined): number | null => {
    if (!d || !d.sessions.length) return null;
    const cap = d.sessions.reduce((a, s) => a + (s.slots || 0), 0);
    const bk = d.sessions.reduce((a, s) => a + (s.booked || 0), 0);
    return cap > 0 ? Math.round((bk / cap) * 100) : null;
  };

  return (
    <Box>
      <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fill, minmax(232px, 1fr))"
        gap={2.5}
      >
        {months.map((mo) => (
          <Box key={mo.label}>
            <Box display="flex" alignItems="baseline" justifyContent="space-between" mb={0.75}>
              <Typography variant="caption" fontWeight={800}>{mo.label}</Typography>
              <Typography variant="caption" color="text.disabled">
                {mo.sessions || '—'}
              </Typography>
            </Box>
            <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" gap="3px">
              {DOW.map((dw, i) => (
                <Typography
                  key={`${mo.label}-h-${i}`}
                  variant="caption"
                  align="center"
                  sx={{ color: 'text.disabled', fontSize: 9, lineHeight: 1.6 }}
                >
                  {dw[0]}
                </Typography>
              ))}
              {mo.cells.map((date, i) => {
                const d = date ? byDate.get(date) : undefined;
                const count = d?.sessions.length ?? 0;
                const band = bandFor(dayFill(d));
                const dark = band ? BANDS.indexOf(band) >= 3 : false;
                return (
                  <Box
                    key={date ?? `${mo.label}-pad-${i}`}
                    onClick={(e) => {
                      if (!d) return;
                      setActive(d);
                      setAnchor(e.currentTarget);
                    }}
                    title={
                      date && count
                        ? `${date} · ${count} session${count === 1 ? '' : 's'} · ` +
                          `${d!.sessions.reduce((a, x) => a + x.booked, 0)} booked of ` +
                          `${d!.sessions.reduce((a, x) => a + x.slots, 0)} · ${dayFill(d) ?? '—'}% full`
                        : undefined
                    }
                    sx={{
                      height: 28,
                      borderRadius: 0.75,
                      bgcolor: !date
                        ? 'transparent'
                        : count
                          ? band?.step ?? GHOST
                          : '#f5f5f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                      cursor: count ? 'pointer' : 'default',
                      transition: 'transform .1s ease',
                      '&:hover': count ? { transform: 'scale(1.18)', zIndex: 1 } : {},
                    }}
                  >
                    {date && (
                      // Just the date. Colour carries how full the day was, and
                      // the counts live in the hover and the click-through —
                      // printing a number in every one of 365 cells is the
                      // "value on every data point" problem, and it competed
                      // with the heat for the same glance.
                      <Typography
                        variant="caption"
                        fontWeight={count ? 700 : 400}
                        sx={{
                          fontSize: 10,
                          lineHeight: 1,
                          color: dark
                            ? 'rgba(255,255,255,.92)'
                            : count
                              ? '#1a1a1a'
                              : 'text.disabled',
                        }}
                      >
                        {Number(date.slice(8, 10))}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Scale legend — a sequential ramp is unreadable without one, and the
          bands are printed because "darker = fuller" alone does not tell a
          reader whether dark means 40% or 90%. */}
      <Box display="flex" alignItems="center" gap={2} mt={2.5} flexWrap="wrap">
        <Box display="flex" alignItems="center" gap={0.75}>
          <Typography variant="caption" color="text.secondary">How full:</Typography>
          {BANDS.map((b) => (
            <Box key={b.label} display="flex" alignItems="center" gap={0.4}>
              <Box sx={{ width: 14, height: 10, borderRadius: 0.5, bgcolor: b.step }} />
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                {b.label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ width: 14, height: 10, borderRadius: 0.5, bgcolor: '#f5f5f6' }} />
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
            Not teaching
          </Typography>
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
          Hover a day for sessions and bookings; click for the full list.
        </Typography>
      </Box>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { p: 2, maxWidth: 340, borderRadius: 2 } } }}
      >
        {active && (
          <>
            <Typography variant="subtitle2" fontWeight={700}>
              {new Date(`${active.date}T00:00:00Z`).toLocaleDateString('en-GB', {
                weekday: 'long', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
              })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {active.sessions.length} session{active.sessions.length === 1 ? '' : 's'}
              {' · '}
              {active.sessions.reduce((a, s) => a + s.booked, 0)} booked of{' '}
              {active.sessions.reduce((a, s) => a + s.slots, 0)}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
              {active.sessions
                .slice()
                .sort((a, b) => a.time.localeCompare(b.time))
                .map((s) => (
                  <Box key={s._id} display="flex" justifyContent="space-between" gap={2} py={0.4}>
                    <Box minWidth={0}>
                      <Typography variant="body2" fontWeight={600} noWrap>{s.className}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.time} · {s.durationMins}m · {s.sessionType}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
                      {s.booked}/{s.slots}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </>
        )}
      </Popover>
    </Box>
  );
};

export default ScheduleCalendar;
